// Carries a hero prompt where localStorage cannot: to another origin (the
// ifttt.ai homepage hands off to ifttt.com), or past a localStorage write that
// failed (storage full or blocked). The fragment never reaches a server and
// survives the redirect chain; the receiving page parks it with the regular
// pending-prompt helpers and strips it from the address bar.
//
// A hash prompt only pre-fills /create. It never carries an autostart token,
// so a crafted link cannot auto-send a chat for a signed-in visitor.
import {
  isPendingPromptSource,
  sanitizePrompt,
  savePendingPrompt,
  type PendingPromptEntry,
  type PendingPromptSource,
} from "app/utils/pending_prompt_storage"

const PROMPT_PARAM = "prompt"
const SOURCE_PARAM = "source"

export const withPromptHash = (url: string, prompt: string, source: PendingPromptSource): string => {
  const target = new URL(url, window.location.href)
  target.hash = `${PROMPT_PARAM}=${encodeURIComponent(sanitizePrompt(prompt))}&${SOURCE_PARAM}=${source}`
  return target.href
}

const hasPromptHash = (hash: string) => new URLSearchParams(hash.slice(1)).has(PROMPT_PARAM)

// For analytics: a recorded URL must never carry the user's prompt text.
export const withoutPromptHash = (url: string): string => {
  const target = new URL(url)
  if (!hasPromptHash(target.hash)) return url
  target.hash = ""
  return target.href
}

const readHashPrompt = (): PendingPromptEntry | null => {
  const { hash } = window.location
  if (!hasPromptHash(hash)) return null
  window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search)

  const params = new URLSearchParams(hash.slice(1))
  const prompt = sanitizePrompt(params.get(PROMPT_PARAM)!)
  if (prompt.length === 0) return null
  const source = params.get(SOURCE_PARAM)
  // The empty token never matches an ?autostart= param, so the prompt only pre-fills.
  return {
    prompt,
    source: isPendingPromptSource(source) ? source : "home_prompt_hero",
    createdAt: Date.now(),
    token: "",
  }
}

// Lives only for this page load: the fallback for /create when storage refused the prompt.
let unparkedPrompt: PendingPromptEntry | null = null

// Runs before analytics start, so the fragment is gone before anything can record the URL.
export const parkPromptFromHash = () => {
  const entry = readHashPrompt()
  if (entry && !savePendingPrompt(entry.prompt, entry.source)) unparkedPrompt = entry
}

export const takePromptFromHash = (): PendingPromptEntry | null => {
  const entry = unparkedPrompt
  unparkedPrompt = null
  return entry
}
